// GA4 limits param values to 100 characters
const MAX_PARAM_LENGTH = 100;

const truncate = (value: string): string =>
  value.length > MAX_PARAM_LENGTH ? value.slice(0, MAX_PARAM_LENGTH) : value;

const getElementText = (element: HTMLElement): string => {
  const ariaLabel = element.getAttribute('aria-label');

  if (ariaLabel) return ariaLabel;

  const text = element.innerText?.trim().replace(/\s+/g, ' ');

  if (text) return text;

  const img = element.querySelector('img');

  if (img?.alt) return img.alt;

  return '(no text)';
};

const getSectionName = (element: HTMLElement): string | undefined => {
  const container = element.closest(
    '[data-section], section[id], header, footer, nav, [id]'
  ) as HTMLElement | null;

  if (!container) return undefined;

  return (
    container.dataset.section || container.id || container.tagName.toLowerCase()
  );
};

export const trackEvent = (
  eventName: string,
  params: Record<string, unknown> = {}
): void => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', eventName, params);
};

const handleDocumentClick = (event: Event): void => {
  const target = event.target as Element | null;

  if (!target?.closest) return;

  const clickable = target.closest(
    'a, button, [role="button"]'
  ) as HTMLElement | null;

  if (!clickable) return;

  const isLink = clickable.tagName === 'A';
  const href = isLink ? (clickable as HTMLAnchorElement).href : undefined;

  let isOutbound = false;

  if (href) {
    try {
      isOutbound = new URL(href).host !== window.location.host;
    } catch {
      isOutbound = false;
    }
  }

  const params: Record<string, unknown> = {
    element_type: isLink ? 'link' : 'button',
    element_text: truncate(getElementText(clickable)),
    page_path: window.location.pathname,
    transport_type: 'beacon',
  };

  if (href) {
    params.link_url = truncate(href);
    params.outbound = isOutbound;
  }

  if (clickable.id) {
    params.element_id = clickable.id;
  }

  const section = getSectionName(clickable);

  if (section) {
    params.section = truncate(section);
  }

  trackEvent('ui_click', params);
};

let isClickTrackingInitialized = false;

export const initClickTracking = (): void => {
  if (typeof document === 'undefined' || isClickTrackingInitialized) return;

  isClickTrackingInitialized = true;
  // Capture phase so clicks are tracked even if a handler stops propagation
  document.addEventListener('click', handleDocumentClick, true);
};
